import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Whether `value` can be an organization id. Organization ids are UUIDs, and
 * the server answers any other `X-Context-Id` with a 400 that fails the whole
 * request.
 */
export function isOrganizationId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value,
  );
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Money, in a stated currency. **The currency is required.**
 *
 * It used to default to `"USD"`, and that default was the whole bug: a caller
 * that forgot the argument produced a confident `$1,234.50` for a Canadian
 * customer, which is wrong by an exchange rate and looks entirely correct. The
 * omission was invisible at the call site and invisible on screen.
 *
 * Required, it is a compile error instead. A caller that genuinely has no
 * currency to hand passes `DEFAULT_REPORTING_CURRENCY`, which is greppable and
 * says so.
 *
 * `Intl` distinguishes the two dollars on its own — `$1,234.50` for USD,
 * `CA$1,234.50` for CAD — so no call site needs to disambiguate by hand.
 */
export function formatCurrency(
  amount: number,
  currency: string,
  locale: string = "en-US",
): string {
  // A figure that arrives with no currency is a data fault, and `Intl` throws
  // on an empty code -- which took a whole page down to the error boundary
  // when one Desk ticket held a realised saving in no currency. The number is
  // still shown, with no symbol, so it cannot pass for money in a currency it
  // was never in.
  if (!currency) {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * A short money label for stat tiles and chart axes: `$1.2K`, `C$840`.
 *
 * The currency argument is the point. These call sites used template literals
 * with a hardcoded `$` and their own `/1000` arithmetic, which was correct only
 * while every figure was USD. Cost is now resolved per caller, so the label has
 * to follow the number. `Intl` also localizes the suffix and places the symbol
 * correctly, which hand-built strings do not.
 */
export function formatCompactCurrency(
  amount: number,
  currency: string,
  locale: string = "en-US",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(amount);
}

export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat("en-US", options).format(value);
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * A date or timestamp as a `Date`, reading a bare calendar day as that day.
 *
 * `new Date("2026-09-30")` is midnight UTC, which is the evening of the 29th
 * for anyone west of UTC, so a due date, a billing day or a cost day showed a
 * day early there. A `YYYY-MM-DD` string is a day, not an instant, and is
 * built in local time instead. Anything with a time in it is an instant and
 * is parsed as one.
 */
export function parseDate(date: Date | string): Date {
  if (typeof date !== "string") return date;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  return new Date(date);
}

/**
 * The product's one date style: `Sep 30, 2026`. Every page shows dates this
 * way, so a date reads the same on Billing, in Desk and on a credential.
 */
export function formatDate(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    ...options,
  }).format(parseDate(date));
}

/** An instant in the same style, with the time: `Sep 30, 2026, 3:04 PM`. */
export function formatDateTime(
  date: Date | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  return formatDate(date, { hour: "numeric", minute: "2-digit", ...options });
}

/**
 * A calendar day that may be missing, for a cell or a caption: `Sep 30, 2026`,
 * or an empty string. A value that is not a date comes back as it was, so a
 * bad row shows what it holds rather than "Invalid Date".
 */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseDate(iso.length > 10 && iso[10] === "T" ? iso.slice(0, 10) : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatDate(d);
}

export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(d);
}

/**
 * Human-readable countdown to an expiry timestamp, e.g. "Expires in 5 days",
 * "Expires in 1 day", "Expires today", or "Expired" once the moment has passed.
 * Uses whole days remaining (floor) so "Expires today" covers anything under 24h.
 */
export function formatExpiresIn(expiresAt: Date | string): string {
  const d = typeof expiresAt === "string" ? new Date(expiresAt) : expiresAt;
  const diffMs = d.getTime() - Date.now();
  if (diffMs < 0) return "Expired";
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days === 0) return "Expires today";
  if (days === 1) return "Expires in 1 day";
  return `Expires in ${days} days`;
}

export const PROVIDER_COLORS: Record<string, string> = {
  aws: "#ff9900",
  azure: "#0078d4",
  gcp: "#4285f4",
  openai: "#10a37f",
  anthropic: "#d97706",
  cursor: "#6b7280",
  openrouter: "#8b5cf6",
  snowflake: "#29b5e8",
  digitalocean: "#0080ff",
};

/**
 * Brand colour for a known provider. The values in PROVIDER_COLORS are frozen
 * vendor brand marks and are deliberately allowed to clash with the theme.
 * The *fallback* is not a brand, so it is themed — a fixed grey either vanishes
 * on a near-black canvas or glares on a light one.
 */
export function getProviderColor(provider: string): string {
  return (
    PROVIDER_COLORS[provider.toLowerCase()] || "hsl(var(--muted-foreground))"
  );
}

export function getProviderName(provider: string): string {
  const names: Record<string, string> = {
    aws: "Amazon AWS",
    azure: "Microsoft Azure",
    gcp: "Google Cloud",
    // The AI providers bill through the same pipeline and reached the UI as
    // their raw slugs -- a chart legend reading "openai" beside "Microsoft
    // Azure" looks like a bug rather than a brand.
    openai: "OpenAI",
    anthropic: "Anthropic",
    openrouter: "OpenRouter",
    cursor: "Cursor",
    snowflake: "Snowflake",
    digitalocean: "DigitalOcean",
  };
  return names[provider.toLowerCase()] || provider;
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Currency tick label for a chart axis: `$0`, `$200k`, `CA$1.2M`.
 *
 * Takes a currency because the amounts feeding these axes are resolved per
 * caller now. Defaults to USD so existing callers are unchanged.
 */
export function formatAxisCurrency(value: number, currency = "USD"): string {
  if (!Number.isFinite(value)) return "";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  // Symbol only, from the same source as every other label, so a currency whose
  // symbol is not "$" cannot end up rendered as one.
  const symbol = formatCurrency(0, currency).replace(/[\d.,\s]/g, "");

  if (abs === 0) return `${symbol}0`;
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    return `${sign}${symbol}${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    return `${sign}${symbol}${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `${sign}${symbol}${abs}`;
}
